import React from 'react';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Checklist } from '../../components/Checklist';
import { ORG_RECOVER_CHECKLIST } from '../../utils/constants';
import { RefreshCw, AlertTriangle, Zap, ArrowRight, ArrowLeft, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
export function Recover() {
  const breadcrumbs = [{
    label: 'Protocols',
    path: '/'
  }, {
    label: 'Organization Protocol',
    path: '/organization'
  }, {
    label: 'Recovery & Improvement'
  }];
  return <Layout breadcrumbs={breadcrumbs} data-id="element-4239">
      <div className="container mx-auto px-4 py-12" data-id="element-4240">
        {/* Header */}
        <div className="mb-12" data-id="element-4241">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-500 text-xs font-medium mb-4 border border-cyan-500/20" data-id="element-4242">
            <RefreshCw className="h-3 w-3 mr-2" data-id="element-4243" />
            Domain 6 of 6
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-100 mb-4" data-id="element-4244">
            Recovery & Improvement
          </h1>
          <p className="text-xl text-slate-400 max-w-3xl" data-id="element-4245">
            Plan for recovery from incidents and continuously improve your
            security posture through lessons learned and resilience planning.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-4246">
          <div className="lg:col-span-2 space-y-12" data-id="element-4247">
            {/* What This Domain Covers */}
            <section data-id="element-4248">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-4249">
                What This Domain Covers
              </h2>
              <ul className="space-y-2 text-slate-300" data-id="element-4250">
                <li className="flex items-start" data-id="element-4251">
                  <span className="text-cyan-500 mr-2" data-id="element-4252">•</span>
                  Recovery planning and prioritization
                </li>
                <li className="flex items-start" data-id="element-4253">
                  <span className="text-cyan-500 mr-2" data-id="element-4254">•</span>
                  Backup restoration and testing
                </li>
                <li className="flex items-start" data-id="element-4255">
                  <span className="text-cyan-500 mr-2" data-id="element-4256">•</span>
                  Business continuity and resilience
                </li>
                <li className="flex items-start" data-id="element-4257">
                  <span className="text-cyan-500 mr-2" data-id="element-4258">•</span>
                  Post-incident lessons learned
                </li>
                <li className="flex items-start" data-id="element-4259">
                  <span className="text-cyan-500 mr-2" data-id="element-4260">•</span>
                  Continuous security improvement
                </li>
              </ul>
            </section>

            {/* Minimum Baseline Controls */}
            <section data-id="element-4261">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-4262">
                Minimum Baseline Controls
              </h2>
              <div className="space-y-3" data-id="element-4263">
                <ControlItem title="Define Recovery Priorities" description="List critical systems in order of recovery priority (what must be restored first)." data-id="element-4264" />
                <ControlItem title="Test Backup Restoration" description="Verify backups work by performing test restores at least quarterly." data-id="element-4265" />
                <ControlItem title="Document Recovery Procedures" description="Write step-by-step instructions for restoring each critical system." data-id="element-4266" />
                <ControlItem title="Establish Communication Plan" description="Define how to notify employees and customers during recovery." data-id="element-4267" />
                <ControlItem title="Conduct Post-Incident Reviews" description="After every incident, document what happened and what to improve." data-id="element-4268" />
                <ControlItem title="Update Security Controls" description="Implement improvements identified in post-incident reviews." data-id="element-4269" />
              </div>
            </section>

            {/* Best Practice Controls */}
            <section data-id="element-4270">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-4271">
                Best Practice Controls
              </h2>
              <div className="space-y-3" data-id="element-4272">
                <ControlItem title="Create Business Continuity Plan" description="Comprehensive plan for maintaining operations during disruptions." data-id="element-4273" />
                <ControlItem title="Define RTO and RPO" description="Set Recovery Time Objective (how fast) and Recovery Point Objective (how much data loss) for each system." data-id="element-4274" />
                <ControlItem title="Establish Alternate Operations" description="Identify backup locations, manual processes, or alternate vendors if primary systems fail." data-id="element-4275" />
                <ControlItem title="Test Recovery Annually" description="Conduct full disaster recovery exercise to verify plans work under pressure." data-id="element-4276" />
                <ControlItem title="Implement Lessons Learned Process" description="Formal process to capture, prioritize, and implement improvements after incidents." data-id="element-4277" />
                <ControlItem title="Track Security Metrics" description="Measure improvement over time (incident count, time to detect, time to recover)." data-id="element-4278" />
                <ControlItem title="Conduct Quarterly Reviews" description="Review security posture, incidents, and improvements with leadership." data-id="element-4279" />
                <ControlItem title="Maintain Recovery Runbooks" description="Detailed procedures for recovering each critical system and service." data-id="element-4280" />
                <ControlItem title="Establish Vendor Failover" description="Identify alternate vendors or services if primary providers fail." data-id="element-4281" />
                <ControlItem title="Document Dependencies" description="Map system dependencies so you know what must be restored in what order." data-id="element-4282" />
              </div>
            </section>

            {/* Common Mistakes */}
            <section data-id="element-4283">
              <h2 className="text-2xl font-bold text-slate-100 mb-4 flex items-center" data-id="element-4284">
                <AlertTriangle className="h-6 w-6 text-red-500 mr-3" data-id="element-4285" />
                Common Mistakes
              </h2>
              <div className="space-y-3 text-slate-400" data-id="element-4286">
                <p className="flex items-start" data-id="element-4287">
                  <span className="text-red-500 mr-2" data-id="element-4288">✗</span>
                  Assuming backups work without testing (many organizations
                  discover backup failures during recovery)
                </p>
                <p className="flex items-start" data-id="element-4289">
                  <span className="text-red-500 mr-2" data-id="element-4290">✗</span>
                  No clear recovery priorities (trying to restore everything at
                  once causes chaos)
                </p>
                <p className="flex items-start" data-id="element-4291">
                  <span className="text-red-500 mr-2" data-id="element-4292">✗</span>
                  Skipping post-incident reviews (you'll repeat the same
                  mistakes)
                </p>
                <p className="flex items-start" data-id="element-4293">
                  <span className="text-red-500 mr-2" data-id="element-4294">✗</span>
                  Not communicating during recovery (employees and customers
                  need updates)
                </p>
                <p className="flex items-start" data-id="element-4295">
                  <span className="text-red-500 mr-2" data-id="element-4296">✗</span>
                  Treating security as one-time project (it's continuous
                  improvement)
                </p>
              </div>
            </section>

            {/* Quick Wins */}
            <section data-id="element-4297">
              <h2 className="text-2xl font-bold text-slate-100 mb-4 flex items-center" data-id="element-4298">
                <Zap className="h-6 w-6 text-yellow-500 mr-3" data-id="element-4299" />
                Quick Wins (7 Days)
              </h2>
              <div className="space-y-3" data-id="element-4300">
                <QuickWin day="Day 1-2" action="List critical systems in order of recovery priority" data-id="element-4301" />
                <QuickWin day="Day 3-4" action="Perform test restore of one critical system" data-id="element-4302" />
                <QuickWin day="Day 5-6" action="Document recovery procedure for top 3 systems" data-id="element-4303" />
                <QuickWin day="Day 7" action="Schedule quarterly backup testing and security reviews" data-id="element-4304" />
              </div>
            </section>

            {/* Continuous Improvement Cycle */}
            <section data-id="element-4305">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-4306">
                Continuous Improvement Cycle
              </h2>
              <div className="space-y-4" data-id="element-4307">
                <div className="flex items-start" data-id="element-4308">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-500 text-slate-950 font-bold flex items-center justify-center mr-4" data-id="element-4309">
                    1
                  </div>
                  <div data-id="element-4310">
                    <h4 className="font-bold text-slate-200 mb-1" data-id="element-4311">Measure</h4>
                    <p className="text-slate-400 text-sm" data-id="element-4312">
                      Track security metrics and incident trends
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-4313">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-500 text-slate-950 font-bold flex items-center justify-center mr-4" data-id="element-4314">
                    2
                  </div>
                  <div data-id="element-4315">
                    <h4 className="font-bold text-slate-200 mb-1" data-id="element-4316">Analyze</h4>
                    <p className="text-slate-400 text-sm" data-id="element-4317">
                      Identify gaps, weaknesses, and improvement opportunities
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-4318">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-500 text-slate-950 font-bold flex items-center justify-center mr-4" data-id="element-4319">
                    3
                  </div>
                  <div data-id="element-4320">
                    <h4 className="font-bold text-slate-200 mb-1" data-id="element-4321">Improve</h4>
                    <p className="text-slate-400 text-sm" data-id="element-4322">
                      Implement controls, update procedures, train staff
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-4323">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-500 text-slate-950 font-bold flex items-center justify-center mr-4" data-id="element-4324">
                    4
                  </div>
                  <div data-id="element-4325">
                    <h4 className="font-bold text-slate-200 mb-1" data-id="element-4326">Repeat</h4>
                    <p className="text-slate-400 text-sm" data-id="element-4327">
                      Security is never "done" - continuously adapt to new
                      threats
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-4328">
            <Checklist id="org-recover" title="Implementation Checklist" items={ORG_RECOVER_CHECKLIST} data-id="element-4329" />

            <Card className="p-6 bg-slate-900/50 border-slate-800" data-id="element-4330">
              <h3 className="font-bold text-slate-100 mb-4" data-id="element-4331">
                Recovery Time Objectives
              </h3>
              <div className="space-y-3 text-sm" data-id="element-4332">
                <div data-id="element-4333">
                  <span className="text-slate-400" data-id="element-4334">Critical Systems:</span>
                  <span className="text-cyan-400 ml-2 font-bold" data-id="element-4335">4 hours</span>
                </div>
                <div data-id="element-4336">
                  <span className="text-slate-400" data-id="element-4337">Important Systems:</span>
                  <span className="text-cyan-400 ml-2 font-bold" data-id="element-4338">24 hours</span>
                </div>
                <div data-id="element-4339">
                  <span className="text-slate-400" data-id="element-4340">Standard Systems:</span>
                  <span className="text-cyan-400 ml-2 font-bold" data-id="element-4341">72 hours</span>
                </div>
                <p className="text-slate-500 text-xs mt-3" data-id="element-4342">
                  Adjust based on your business requirements
                </p>
              </div>
            </Card>

            <Card className="p-6 bg-slate-900/50 border-slate-800" data-id="element-4343">
              <h3 className="font-bold text-slate-100 mb-4" data-id="element-4344">
                Download Resources
              </h3>
              <Link to="/downloads/organization-pack" data-id="element-4345">
                <Button variant="secondary" fullWidth className="mb-3" data-id="element-4346">
                  <Download className="h-4 w-4 mr-2" data-id="element-4347" />
                  Recovery Plan Template
                </Button>
              </Link>
              <Link to="/downloads/organization-pack" data-id="element-4348">
                <Button variant="secondary" fullWidth data-id="element-4349">
                  <Download className="h-4 w-4 mr-2" data-id="element-4350" />
                  Restore Test Schedule
                </Button>
              </Link>
            </Card>

            <Card className="p-6 bg-slate-900/50 border-slate-800" data-id="element-4351">
              <h3 className="font-bold text-slate-100 mb-4" data-id="element-4352">
                You've Completed All Domains!
              </h3>
              <p className="text-sm text-slate-400 mb-4" data-id="element-4353">
                Return to the overview or explore additional resources.
              </p>
              <Link to="/organization" data-id="element-4354">
                <Button variant="outline" fullWidth className="mb-3" data-id="element-4355">
                  <ArrowLeft className="h-4 w-4 mr-2" data-id="element-4356" />
                  Back to Overview
                </Button>
              </Link>
              <Link to="/downloads/organization-pack" data-id="element-4357">
                <Button fullWidth data-id="element-4358">
                  <Download className="h-4 w-4 mr-2" data-id="element-4359" />
                  Download Full Pack
                </Button>
              </Link>
            </Card>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-12 flex justify-between items-center border-t border-slate-800 pt-8" data-id="element-4360">
          <Link to="/organization/respond" data-id="element-4361">
            <Button variant="outline" data-id="element-4362">
              <ArrowLeft className="h-4 w-4 mr-2" data-id="element-4363" />
              Previous: Incident Response
            </Button>
          </Link>
          <Link to="/organization" data-id="element-4364">
            <Button data-id="element-4365">
              Complete Protocol
              <ArrowRight className="h-4 w-4 ml-2" data-id="element-4366" />
            </Button>
          </Link>
        </div>
      </div>
    </Layout>;
}
function ControlItem({
  title,
  description
}: {
  title: string;
  description: string;
}) {
  return <Card className="p-4 bg-slate-900/50 border-slate-800" data-id="element-4367">
      <h4 className="font-bold text-slate-100 text-sm mb-1" data-id="element-4368">{title}</h4>
      <p className="text-slate-400 text-sm" data-id="element-4369">{description}</p>
    </Card>;
}
function QuickWin({
  day,
  action
}: {
  day: string;
  action: string;
}) {
  return <div className="flex items-start" data-id="element-4370">
      <span className="text-yellow-500 font-mono text-sm mr-3 mt-0.5" data-id="element-4371">
        {day}
      </span>
      <span className="text-slate-300 text-sm" data-id="element-4372">{action}</span>
    </div>;
}